import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserShield,
  faPaperPlane,
  faRedo,
  faSpinner,
  faKey,
  faCheckCircle,
  faArrowRight
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import ApiServices from "../Services/ApiServices";
import Loader from "../Shared/Loader";
import { storageHelper } from "../../Helpers/storageHelper";
import { toastHelper } from "../../Helpers/toastHelper";

const SuperAdminnOTP = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const phoneFromState = location.state?.phone || "";
  const phoneFromStorage = storageHelper.getItem("phone") || "";
  const phone = phoneFromState || phoneFromStorage;

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const inputRefs = useRef([]);

  // Ensure only super admins can access this page
  useEffect(() => {
    const userType = storageHelper.getItem("user_type");
    if (userType !== "superadmin" && userType !== "SuperAdmin") {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Send OTP automatically on mount if phone exists
  useEffect(() => {
    if (phone) {
      handleSendOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  // Cooldown effect
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSendOtp = async () => {
    if (!phone) {
      setMessage("رقم الهاتف غير متوفر، يرجى تسجيل الدخول مرة أخرى.");
      toastHelper.error("رقم الهاتف غير متوفر، يرجى تسجيل الدخول مرة أخرى.");
      return;
    }

    if (resendCooldown > 0) return;

    setSending(true);
    setMessage("");
    try {
      const response = await ApiServices.post("WhatsApp/SendOTP", {
        phone,
        is_super_admin: true,
      });

      const msg = response?.message || response?.msg || "تم إرسال رمز التحقق بنجاح.";
      setMessage(msg);
      toastHelper.success(msg);
      setResendCooldown(60); // 60 seconds cooldown
      // Focus first element if possible
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      const msg =
        err?.msg ||
        err?.message ||
        "حدث خطأ أثناء إرسال رمز التحقق، حاول مرة أخرى.";
      setMessage(msg);
      toastHelper.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(Number(value))) return; // Ensuring it's numeric 

    // Only take the last character entered
    const char = value.slice(-1);

    const newOtp = [...otp];
    newOtp[index] = char;
    setOtp(newOtp);

    // Auto focus next input
    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Enter key
    if (e.key === "Enter") {
      handleVerify(e);
    }
  };

  // Auto-verify when all 6 digits are entered
  useEffect(() => {
    if (otp.every(digit => digit !== "") && otp.join("").length === 6) {
      // Small pause for UX
      const timer = setTimeout(() => {
        handleVerify({ preventDefault: () => { } });
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleVerify = async (e) => {
    if (e) e.preventDefault();

    const otpValue = otp.join("");

    if (otpValue.length < 6) {
      toastHelper.error("يرجى إدخال رمز التحقق كاملاً");
      return;
    }
    if (!phone) {
      toastHelper.error("رقم الهاتف غير متوفر، يرجى تسجيل الدخول مرة أخرى.");
      return;
    }

    setVerifying(true);
    setMessage("");
    try {
      const result = await ApiServices.post("WhatsApp/CheckOTP", {
        otp: otpValue,
        phone,
      });

      if (result === true) {
        storageHelper.setItem("super_admin_otp_verified", "true");
        toastHelper.success("تم التحقق من رمز الدخول بنجاح");
        navigate("/admin/dashboard", { replace: true });
      } else {
        const msg = "رمز التحقق غير صحيح أو منتهي الصلاحية";
        setMessage(msg);
        toastHelper.error(msg);
        // Clear OTP on error for easy retype
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      const msg =
        err?.msg ||
        err?.message ||
        "حدث خطأ أثناء التحقق من الرمز، حاول مرة أخرى.";
      setMessage(msg);
      toastHelper.error(msg);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    let pastedData = e.clipboardData.getData("text/plain");
    // Strip non-digits and pick up to 6 characters
    pastedData = pastedData.replace(/\D/g, "").slice(0, 6);
    if (!pastedData) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);
    // Focus next empty or last input
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  if (!phone) {
    return <Loader text="جاري بجهيز التحقق من صلاحيات الأدمن..." />;
  }

  return (
    <div className="otp-modern-wrapper" dir="rtl">
      <div className="animated-bg"></div>

      <motion.div
        className="otp-card"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
      >
        <div className="otp-card-header">
          <div className="icon-wrapper pulse-animation">
            <FontAwesomeIcon icon={faUserShield} className="shield-icon" />
          </div>
          <h2>تأكيد دخول السوبر أدمن</h2>
          <p>أمان حسابك هو أولويتنا. يرجى إدخال الرمز السري المرسل إليك عبر واتساب للتحقق من هويتك.</p>
        </div>

        <div className="phone-chip">
          <div className="chip-icon">
            <FontAwesomeIcon icon={faWhatsapp} />
          </div>
          <span className="chip-label" dir="ltr">{phone}</span>
          <FontAwesomeIcon icon={faCheckCircle} className="chip-check" />
        </div>

        <form onSubmit={handleVerify} className="otp-form">
          <label className="input-label">
            <FontAwesomeIcon icon={faKey} className="label-icon" />
            رمز التحقق (OTP)
          </label>

          <div className="otp-inputs-container" dir="ltr" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={(el) => (inputRefs.current[index] = el)}
                className={`otp-digit-input ${digit ? 'filled' : ''} ${verifying ? 'disabled' : ''}`}
                disabled={verifying}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <motion.button
            type="submit"
            className={`verify-btn ${verifying ? 'loading' : ''} ${(otp.join("").length === 6) ? 'ready' : ''}`}
            disabled={verifying || otp.join("").length < 6}
            whileHover={{ scale: (otp.join("").length === 6 && !verifying) ? 1.02 : 1 }}
            whileTap={{ scale: (otp.join("").length === 6 && !verifying) ? 0.98 : 1 }}
          >
            {verifying ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>جاري التحقق...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} />
                <span>تأكيد الرمز</span>
              </>
            )}
          </motion.button>
        </form>

        <AnimatePresence>
          {message && (
            <motion.div
              className={`status-message ${message.includes("بنجاح") || message.includes("successfully") ? 'success' : 'error'}`}
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: "auto", marginTop: 16 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
            >
              {message}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="resend-section">
          <p>لم يصلك الرمز؟</p>
          <button
            type="button"
            onClick={handleSendOtp}
            disabled={sending || resendCooldown > 0}
            className={`resend-btn ${(sending || resendCooldown > 0) ? 'disabled' : ''}`}
          >
            {sending ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> جاري...</>
            ) : resendCooldown > 0 ? (
              `إعادة الإرسال بعد (${resendCooldown}s)`
            ) : (
              <>إعادة إرسال الكود <FontAwesomeIcon icon={faRedo} className="redo-icon" /></>
            )}
          </button>
        </div>

        <button type="button" className="switch-account-btn" onClick={() => navigate("/signin")}>
          <span>تسجيل الدخول بحساب آخر</span>
          <FontAwesomeIcon icon={faArrowRight} style={{ transform: "rotate(180deg)" }} />
        </button>

      </motion.div>

      <style>{`
        .otp-modern-wrapper {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          background-color: #f8fafc;
          overflow: hidden;
          font-family: 'Cairo', 'Tajawal', sans-serif;
        }

        .animated-bg {
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          background: 
            radial-gradient(circle at 15% 50%, rgba(99, 102, 241, 0.08) 0%, transparent 40%),
            radial-gradient(circle at 85% 30%, rgba(168, 85, 247, 0.08) 0%, transparent 40%),
            radial-gradient(circle at 50% 80%, rgba(34, 197, 94, 0.05) 0%, transparent 50%);
          z-index: 0;
        }

        .otp-card {
          width: 90%;
          max-width: 440px;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.4);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02);
          border-radius: 28px;
          padding: 40px 32px;
          position: relative;
          z-index: 1;
        }

        .otp-card-header {
          text-align: center;
          margin-bottom: 28px;
        }

        .icon-wrapper {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: linear-gradient(135deg, #e0e7ff 0%, #ede9fe 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          box-shadow: 0 10px 25px rgba(99, 102, 241, 0.2);
          position: relative;
        }

        .icon-wrapper::after {
          content: "";
          position: absolute;
          width: 100%; height: 100%;
          border-radius: 50%;
          border: 2px solid #6366f1;
          opacity: 0.3;
          animation: pulseIcon 2s infinite ease-out;
        }

        @keyframes pulseIcon {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(1.4); opacity: 0; }
        }

        .shield-icon {
          font-size: 32px;
          color: #4f46e5;
        }

        .otp-card-header h2 {
          font-size: 24px;
          font-weight: 800;
          color: #1e293b;
          margin: 0 0 10px;
        }

        .otp-card-header p {
          font-size: 14px;
          color: #64748b;
          line-height: 1.6;
          margin: 0;
        }

        .phone-chip {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          background: #f1f5f9;
          border-radius: 16px;
          padding: 12px 20px;
          margin-bottom: 28px;
          border: 1px solid #e2e8f0;
        }

        .chip-icon {
          color: #25d366;
          font-size: 18px;
          display: flex;
        }

        .chip-label {
          font-family: monospace;
          font-size: 16px;
          font-weight: 600;
          color: #0f172a;
          letter-spacing: 0.5px;
        }

        .chip-check {
          color: #10b981;
          font-size: 15px;
          margin-right: auto;
        }

        .otp-form {
          margin-bottom: 24px;
        }

        .input-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 700;
          color: #334155;
          margin-bottom: 12px;
        }

        .label-icon {
          color: #6366f1;
        }

        .otp-inputs-container {
          display: flex;
          gap: 8px;
          justify-content: center;
          margin-bottom: 24px;
        }

        .otp-digit-input {
          width: 46px;
          height: 56px;
          border-radius: 12px;
          border: 2px solid #cbd5e1;
          background: white;
          font-size: 24px;
          font-weight: 700;
          font-family: monospace;
          text-align: center;
          color: #0f172a;
          transition: all 0.2s ease;
          outline: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          padding: 0;
        }

        .otp-digit-input:focus {
          border-color: #6366f1;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }

        .otp-digit-input.filled {
          background: #f8fafc;
          border-color: #94a3b8;
        }
        
        .otp-digit-input.filled:focus {
          border-color: #6366f1;
        }

        .otp-digit-input.disabled {
          opacity: 0.6;
          pointer-events: none;
        }

        .verify-btn {
          width: 100%;
          background: #94a3b8;
          color: white;
          border: none;
          border-radius: 14px;
          padding: 16px;
          font-size: 16px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          cursor: not-allowed;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .verify-btn.ready {
          background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
          box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
          cursor: pointer;
        }

        .verify-btn.ready:hover {
          box-shadow: 0 10px 25px rgba(99, 102, 241, 0.4);
        }

        .verify-btn.loading {
          background: #6366f1;
          opacity: 0.8;
          cursor: wait;
        }

        .status-message {
          padding: 12px 16px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          margin-bottom: 20px;
          overflow: hidden;
        }

        .status-message.success {
          background: #ecfdf5;
          color: #059669;
          border: 1px solid #a7f3d0;
        }

        .status-message.error {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .resend-section {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 14px;
          color: #64748b;
          margin-bottom: 24px;
        }

        .resend-btn {
          background: none;
          border: none;
          color: #6366f1;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          padding: 0;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: inherit;
          transition: color 0.2s;
        }

        .resend-btn:hover:not(.disabled) {
          color: #4f46e5;
          text-decoration: underline;
        }

        .resend-btn.disabled {
          color: #94a3b8;
          cursor: default;
        }

        .redo-icon {
          font-size: 12px;
        }

        .switch-account-btn {
          width: 100%;
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #475569;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .switch-account-btn:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #1e293b;
        }
      `}</style>
    </div>
  );
};

export default SuperAdminnOTP;
